/**
 * Application shell — structural only. No styling lives here; the visual layer is
 * `@/components/layout/AppLayout` and `@/components/common/*`, owned by the design agent.
 *
 * Responsibilities:
 *  - run `authStore.bootstrap()` exactly once on mount,
 *  - adopt the signed-in user's locale,
 *  - open the optional realtime socket (§6.7),
 *  - wrap everything in the global error boundary,
 *  - render the route tree.
 */

import { useEffect, type ErrorInfo, type ReactElement } from 'react';

import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { useSyncLocaleFromUser } from '@/i18n';
import AppRoutes from '@/router';
import { useAddressLocale } from '@/seo/useLocalePath';
import { useAuthStore } from '@/stores/authStore';

/**
 * Single place a render crash is reported. Deliberately local-only: this is a self-hosted
 * product and nothing about a user's trading should be shipped to a third party.
 */
function reportRenderError(error: Error, info: ErrorInfo): void {
  // eslint-disable-next-line no-console
  console.error('[HopeBot] Unhandled render error', error, info.componentStack);
}

/**
 * Session restore + optional realtime. Renders nothing — it only wires effects, so a state
 * change here never re-renders the route tree.
 */
function SessionBootstrap(): null {
  const bootstrap = useAuthStore((state) => state.bootstrap);

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  // Adopt `user.locale` from `GET /auth/me` unless this device has an explicit preference.
  useSyncLocaleFromUser();
  // A language address (`/tr/…`) reached inside the app reads in its language.
  useAddressLocale();

  return null;
}

export default function App(): ReactElement {
  return (
    <ErrorBoundary onError={reportRenderError}>
      <SessionBootstrap />
      <AppRoutes />
    </ErrorBoundary>
  );
}
